import {useEffect, useId, useRef, useState, type Ref} from 'react';
import {ApiError, ApplicationContext, createAttorneyDraft, DraftDetails, loadAttorneyDraft, saveAttorneyDraft} from './api';
import {OrderDocuments} from './OrderDocuments';
import {CustomerServiceRequestIntake} from './CustomerServiceRequestIntake';
import type {ProfenzHandoffContext} from './fenzsafeProfenzHandoff';

type DraftRef = NonNullable<ApplicationContext['editable_draft']>;
function readableDraftDetails(value: unknown): value is DraftDetails | null {
  if (value === null) return true;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const details = value as Record<string, unknown>;
  const required = ['case_caption','service_type_code','deadline_date','recipient_name','recipient_type_code','address_line_1','city','admin_area','postal_code','country_code','updated_at'];
  return Number.isSafeInteger(details.version) && Number(details.version) > 0
    && required.every(name => typeof details[name] === 'string' && (details[name] as string).trim().length > 0)
    && typeof details.client_reference === 'string'
    && ['court_reference','case_number','address_line_2'].every(name => details[name] === null || typeof details[name] === 'string')
    && (details.documents_to_serve === undefined || typeof details.documents_to_serve === 'string')
    && details.address_source === 'user_provided' && details.verification_status === 'unverified';
}
function isSavedDraft(value: unknown, expectedVersion: number | null): value is DraftDetails {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const saved = value as Record<string, unknown>;
  const required = ['case_caption','service_type_code','deadline_date','recipient_name','recipient_type_code','address_line_1','city','admin_area','postal_code','country_code','updated_at'];
  const nullable = ['court_reference','case_number','address_line_2'];
  return Number.isSafeInteger(saved.version) && saved.version === (expectedVersion ?? 0) + 1
    && required.every(name => typeof saved[name] === 'string' && (saved[name] as string).trim().length > 0)
    && typeof saved.client_reference === 'string'
    && nullable.every(name => saved[name] === null || typeof saved[name] === 'string')
    && (saved.documents_to_serve === undefined || typeof saved.documents_to_serve === 'string')
    && saved.address_source === 'user_provided' && saved.verification_status === 'unverified';
}
async function observeDraftRead<T>(operation: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_, reject) => {timer = setTimeout(() => reject(new Error('Draft read timed out.')), 15000);}),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
type Props = {
  headingRef?: Ref<HTMLHeadingElement>;
  draft: DraftRef | null;
  onDraft: (draft: DraftRef) => void;
  customerName?: string;
  onEditingChange?: (editing: boolean) => void;
  actionBusy?: boolean;
  onBeginAction?: () => boolean;
  onEndAction?: () => void;
  /**
   * Explicit FenzSafe -> ProFenz handoff context (Issue #114 founder-delegated re-review, comment
   * 5724759851/5725255188, Slice D), additive-only: case caption/client reference/document-title
   * prefill applied ONLY to a genuinely fresh draft that has never been saved (details === null
   * below). The moment any save has happened, the attorney's own data always wins and this is
   * never re-applied -- reloading or editing an existing draft is byte-for-byte unaffected by
   * whether a handoff context happens to be present. No auto-submit: the attorney still walks
   * through every step and clicks Save/Submit themselves. FenzSafe does not transfer real document
   * bytes here (see fenzsafeProfenzHandoff.ts's own doc comment) -- only titles, into the existing
   * "Documents to serve" free-text field the attorney already uses to list papers before uploading
   * them, exactly like the real intake flow already works with no handoff present.
   */
  fenzsafeHandoff?: ProfenzHandoffContext | null;
};

export function AttorneyIntake({draft, onDraft, customerName = '', onEditingChange, fenzsafeHandoff = null, actionBusy, onBeginAction, onEndAction, headingRef}: Props) {
  const [details, setDetails] = useState<DraftDetails | null>(null);
  const [status, setStatus] = useState<'empty'|'loading'|'editing'|'denied'|'failure'>(draft ? 'loading' : 'empty');
  const [reload, setReload] = useState(0);
  const [documentReload, setDocumentReload] = useState(0);
  const key = useRef(`attorney-intake-${crypto.randomUUID()}`);
  const creating = useRef(false);
  const headingId = useId();

  useEffect(() => {
    if (!draft) return;
    let active = true;
    setStatus('loading');
    observeDraftRead(loadAttorneyDraft(draft.order_id)).then(loaded => {
      if (active) {
        if (!readableDraftDetails(loaded) || (loaded === null && draft.details_version !== null)) throw new Error('Draft readback mismatch.');
        setDetails(loaded); setStatus('editing'); onEditingChange?.(false);
      }
    }).catch(error => active && setStatus(error instanceof ApiError && error.status === 403 ? 'denied' : 'failure'));
    return () => {active = false;};
  }, [draft?.order_id, reload, onEditingChange]);

  async function create() {
    if (creating.current) return;
    creating.current = true;
    setStatus('loading');
    try {onDraft(await createAttorneyDraft(key.current));}
    catch (error) {setStatus(error instanceof ApiError && error.status === 403 ? 'denied' : 'failure');}
    finally {creating.current = false;}
  }

  async function save(values: Record<string, string>) {
    if (!draft) throw new Error('No editable draft.');
    // fenzsafeHandoff.sourceMatterId is sent only on the first save of a genuinely fresh,
    // never-saved draft (details === null, the same condition prefillValues below already uses)
    // -- exactly like the prefill itself, this must never re-attribute a conversion to a draft
    // the attorney has already been editing on their own (Issue #114 Task 2, comment 5726525367).
    const expectedVersion = details?.version ?? null;
    const saved = await saveAttorneyDraft(draft.order_id, expectedVersion, values, fenzsafeHandoff && details === null ? fenzsafeHandoff.sourceMatterId : undefined);
    if (!isSavedDraft(saved, expectedVersion)) throw new Error('Draft save response mismatch.');
    setDetails(saved);
    onEditingChange?.(false);
    onDraft({...draft, details_version: saved.version});
  }

  const prefillValues: Partial<DraftDetails> | null = fenzsafeHandoff && details === null
    ? {case_caption: fenzsafeHandoff.caption, client_reference: fenzsafeHandoff.sourceOrderReference, documents_to_serve: fenzsafeHandoff.documents.map((document) => document.name).join('\n')}
    : null;

  return <section className="panel" aria-labelledby={headingId}>
    <h2 id={headingId} ref={headingRef} tabIndex={-1}>Book service with ProFenz</h2>
    {fenzsafeHandoff && <p className="notice" role="status">Starting from your FenzSafe matter &ldquo;{fenzsafeHandoff.caption}&rdquo; (reference {fenzsafeHandoff.sourceOrderReference}) — case details and document titles carry over below; review everything before saving. FenzSafe did not send the actual files, so upload the original papers here as usual.</p>}
    {status === 'empty' && <><p>Start a booking draft to add your papers and service details.</p><button type="button" onClick={create}>Start a booking</button></>}
    {status === 'loading' && <p className="notice" role="status">Loading secure draft…</p>}
    {status === 'denied' && <p className="notice error" role="alert">Access to this draft was denied.</p>}
    {status === 'failure' && <p className="notice error" role="alert">{draft ? 'The draft could not be loaded or saved.' : 'The booking draft could not be confirmed. Keep this page open and retry to recover your draft.'}</p>}
    {!draft && status === 'failure' && <button type="button" onClick={create}>Retry starting booking</button>}
    {draft && (status === 'loading' || status === 'denied' || status === 'failure') && <button type="button" onClick={() => setReload(value => value + 1)}>Reload draft</button>}
    {draft && status === 'editing' && <OrderDocuments key={`documents:${draft.order_id}:${documentReload}`} orderId={draft.order_id}/>}
    {draft && status === 'editing' && <CustomerServiceRequestIntake
      key={`${draft.order_id}:${reload}`}
      customerName={customerName}
      draftConnection={{orderId:draft.order_id,actionBusy,onBeginAction,onEndAction,onDocumentAttached:()=>setDocumentReload(value=>value+1),values: prefillValues ?? details ?? {}, onSave: save, onReload: () => setReload(value => value + 1), onEdit: () => onEditingChange?.(true)}}
    />}
  </section>;
}
